'use client'

import { useEffect, useRef } from 'react'

import { useOptionalPageAnalytics } from '@/app/context/analytics'
import type { EcommerceItem } from '@/app/lib/analytics/types'

interface PerformanceViewItemTrackingProps {
  /** Ítem de e-commerce de la propia ficha, ya con `index=1`. */
  readonly item: EcommerceItem
}

/**
 * Dispara `view_item` una única vez por carga de la PDP
 * (`components-fingerprint/Tabla Detalles Producto.csv` → "Visualización de
 * producto (carga de PDP)"), después del `page_view` que ya dispara
 * `PageAnalyticsProvider` al montar (guía `dictionary/analytic-events.md` →
 * `view_item`: "Disparar después del `page_view`... si los datos llegan de
 * forma asíncrona, esperar a que estén completos" — aquí siempre están
 * completos, `resource` ya llegó resuelto al Server Component).
 *
 * Headless (no pinta nada): hoja cliente aislada para no forzar `PerformancePage`
 * a ser cliente (Constitución, Principio I). Un cambio de PDP (slug distinto)
 * remonta este componente completo, así que el guard de `useRef` solo evita el
 * doble disparo de React Strict Mode en desarrollo, no bloquea páginas nuevas.
 */
export function PerformanceViewItemTracking({ item }: PerformanceViewItemTrackingProps) {
  const analytics = useOptionalPageAnalytics()
  const firedRef = useRef(false)

  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    analytics?.trackViewItem({ currency: item.currency, value: item.price, items: [item] })
    // Un único push por montaje; no debe re-dispararse si `analytics`/`item`
    // cambian de identidad en un re-render sin que la página haya cambiado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}
